import { useCallback, useEffect, useState } from "react";
import {
	type WorkspaceEntry,
	deleteFile,
	getWorkspaceTree,
	gitAdd,
	gitCommit,
	gitDiff,
	gitInit,
	gitLog,
	gitStatus,
	mkdir,
	readFile,
	writeFile,
} from "../lib/agent-api";

export function useWorkspace() {
	const [tree, setTree] = useState<WorkspaceEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [gitInfo, setGitInfo] = useState<unknown>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await getWorkspaceTree();
			setTree(data);
			try {
				const gs = await gitStatus();
				setGitInfo(gs);
			} catch {
				setGitInfo(null);
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to load workspace");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const createFile = useCallback(
		async (path: string, content = "") => {
			await writeFile(path, content);
			await refresh();
		},
		[refresh]
	);

	const createDir = useCallback(
		async (path: string) => {
			await mkdir(path);
			await refresh();
		},
		[refresh]
	);

	const removeFile = useCallback(
		async (path: string) => {
			await deleteFile(path);
			await refresh();
		},
		[refresh]
	);

	const saveFile = useCallback(
		async (path: string, content: string) => {
			await writeFile(path, content);
			await refresh();
		},
		[refresh]
	);

	const initGit = useCallback(async () => {
		await gitInit();
		await refresh();
	}, [refresh]);

	const doGitAdd = useCallback(
		async (filepath: string) => {
			await gitAdd(filepath);
			await refresh();
		},
		[refresh]
	);

	const doGitCommit = useCallback(
		async (message: string) => {
			await gitCommit(message);
			await refresh();
		},
		[refresh]
	);

	const doGitLog = useCallback(async (depth = 20) => {
		return gitLog(depth);
	}, []);

	const doGitDiff = useCallback(async () => {
		return gitDiff();
	}, []);

	return {
		tree,
		loading,
		error,
		gitInfo,
		refresh,
		createFile,
		createDir,
		removeFile,
		saveFile,
		initGit,
		gitAdd: doGitAdd,
		gitCommit: doGitCommit,
		gitLog: doGitLog,
		gitDiff: doGitDiff,
		readFile,
	};
}
